/**
 * ReviewQueue.jsx — decide what each bank charge is.
 *
 * Every row is one decision: give it a category (and optionally a job), then
 * either add it to expenses or mark it as not a business expense. Nothing here
 * writes to the P&L until you press the button — that's the whole point of the
 * queue over auto-classification.
 */
import { useMemo, useState } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Select, MenuItem, Button, Chip, Typography, TextField, InputAdornment,
  ToggleButtonGroup, ToggleButton, Skeleton, Tooltip, IconButton,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import { fmtC, fmtD } from '../../lib/utils';
import { CATEGORIES } from '../expenses/ExpenseForm';
import { useJobs } from '../../hooks/useJobs';
import { useToast } from '../layout/Toast';
import {
  useBankTransactions, useUpdateBankTransaction, usePromoteTransaction,
  useIgnoreTransaction, useUnreviewTransaction,
} from '../../hooks/usePlaid';

// Plaid's category → our nearest expense category. A starting guess that saves
// a click on the common cases; anything ambiguous stays blank on purpose so it
// can't quietly file a charge under the wrong heading.
const CATEGORY_HINTS = {
  FOOD_AND_DRINK: 'Meals',
  TRANSPORTATION: 'Travel',
  TRAVEL: 'Travel',
  GENERAL_MERCHANDISE: 'Supplies',
  BANK_FEES: 'Other',
  RENT_AND_UTILITIES: 'Other',
  ENTERTAINMENT: 'Other',
};

const STATUS_LABEL = { new: 'To review', promoted: 'In expenses', ignored: 'Not an expense' };

function Row({ txn, jobs, onToast }) {
  const [category, setCategory] = useState(txn.category || CATEGORY_HINTS[txn.plaid_category] || '');
  const [jobId, setJobId] = useState(txn.job_id || '');

  const update = useUpdateBankTransaction();
  const promote = usePromoteTransaction();
  const ignore = useIgnoreTransaction();
  const unreview = useUnreviewTransaction();
  const busy = promote.isPending || ignore.isPending || unreview.isPending || update.isPending;

  const isReviewed = txn.review_status !== 'new';
  // Plaid's sign convention: positive means money left the account.
  const isInflow = Number(txn.amount) < 0;

  const handlePromote = async () => {
    if (!category) return onToast('Pick a category first', 'error');
    try {
      await promote.mutateAsync({ id: txn.id, category, jobId: jobId || null });
      onToast(`Added ${txn.merchant_name || txn.name} to expenses`);
    } catch (e) {
      onToast(e.message || 'Could not add to expenses', 'error');
    }
  };

  const handleIgnore = async () => {
    try {
      await ignore.mutateAsync({ id: txn.id });
      onToast('Marked as not an expense');
    } catch (e) {
      onToast(e.message || 'Could not update', 'error');
    }
  };

  const handleUndo = async () => {
    try {
      await unreview.mutateAsync(txn.id);
      onToast('Back in the queue');
    } catch (e) {
      onToast(e.message || 'Could not undo', 'error');
    }
  };

  return (
    <TableRow hover sx={{ opacity: txn.review_status === 'ignored' ? 0.55 : 1 }}>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <Typography variant="body2">{fmtD(txn.date)}</Typography>
        {txn.pending && (
          <Tooltip title="Still pending at the bank. The amount can change when it settles." arrow>
            <Chip label="pending" size="small" sx={{ height: 18, fontSize: 10, mt: 0.25 }} />
          </Tooltip>
        )}
      </TableCell>

      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {txn.merchant_name || txn.name}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {txn.institution_name} {txn.account_mask ? `••${txn.account_mask}` : ''}
          {txn.plaid_category ? ` · ${txn.plaid_category.replaceAll('_', ' ').toLowerCase()}` : ''}
        </Typography>
      </TableCell>

      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: isInflow ? 'success.main' : 'text.primary' }}>
          {isInflow ? `+${fmtC(Math.abs(txn.amount))}` : fmtC(txn.amount)}
        </Typography>
      </TableCell>

      {isReviewed ? (
        <>
          <TableCell colSpan={2}>
            <Chip
              size="small"
              label={STATUS_LABEL[txn.review_status]}
              color={txn.review_status === 'promoted' ? 'success' : 'default'}
              variant="outlined"
            />
            {txn.category && <Chip size="small" label={txn.category} sx={{ ml: 0.5 }} />}
            {txn.job_title && <Chip size="small" label={txn.job_title} sx={{ ml: 0.5 }} variant="outlined" />}
          </TableCell>
          <TableCell align="right">
            <Tooltip title={txn.review_status === 'promoted'
              ? 'Undo — also deletes the expense this created'
              : 'Put back in the queue'} arrow>
              <span>
                <IconButton size="small" onClick={handleUndo} disabled={busy}>
                  <UndoRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </TableCell>
        </>
      ) : (
        <>
          <TableCell sx={{ minWidth: 140 }}>
            <Select
              size="small" fullWidth displayEmpty value={category}
              onChange={(e) => setCategory(e.target.value)}
              sx={{ fontSize: 13 }}
            >
              <MenuItem value=""><em>Category…</em></MenuItem>
              {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </TableCell>

          <TableCell sx={{ minWidth: 160 }}>
            <Select
              size="small" fullWidth displayEmpty value={jobId}
              onChange={(e) => setJobId(e.target.value)}
              sx={{ fontSize: 13 }}
            >
              <MenuItem value=""><em>No job</em></MenuItem>
              {jobs.map((j) => (
                <MenuItem key={j.id} value={j.id}>{j.title || j.client || j.id}</MenuItem>
              ))}
            </Select>
          </TableCell>

          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
            {/* Money arriving books a NEGATIVE expense, which is right for a
                refund and wrong for a client payment (that belongs to invoices).
                Naming the button for the legitimate case stops the other one
                from happening by reflex. */}
            <Tooltip title={isInflow
              ? 'Books a negative expense — correct for a refund. Client payments belong on an invoice, not here.'
              : ''} arrow disableHoverListener={!isInflow}>
              <Button size="small" variant="contained" onClick={handlePromote} disabled={busy}
                sx={{ textTransform: 'none', mr: 0.5 }}>
                {isInflow ? 'Add as refund' : 'Add to expenses'}
              </Button>
            </Tooltip>
            <Button size="small" onClick={handleIgnore} disabled={busy}
              sx={{ textTransform: 'none', color: 'text.secondary' }}>
              Not an expense
            </Button>
          </TableCell>
        </>
      )}
    </TableRow>
  );
}

export default function ReviewQueue() {
  const [status, setStatus] = useState('new');
  const [search, setSearch] = useState('');
  const { toast } = useToast();
  const { data: jobs = [] } = useJobs();

  const filters = useMemo(
    () => ({ reviewStatus: status, search: search.trim() || undefined, limit: 200 }),
    [status, search]
  );
  const { data, isLoading } = useBankTransactions(filters);
  const txns = data?.transactions || [];

  // Jobs list gets long; recent first is the practical order for a charge you
  // are categorizing today.
  const sortedJobs = useMemo(
    () => [...jobs].sort((a, b) => String(b.shootDate || b.createdAt || '').localeCompare(String(a.shootDate || a.createdAt || ''))),
    [jobs]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, height: '100%', minHeight: 0 }}>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
        <ToggleButtonGroup size="small" exclusive value={status} onChange={(_, v) => v && setStatus(v)}>
          <ToggleButton value="new" sx={{ textTransform: 'none', px: 2 }}>To review</ToggleButton>
          <ToggleButton value="promoted" sx={{ textTransform: 'none', px: 2 }}>In expenses</ToggleButton>
          <ToggleButton value="ignored" sx={{ textTransform: 'none', px: 2 }}>Not expenses</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          size="small" placeholder="Search merchant…" value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 18 }} /></InputAdornment> }}
          sx={{ minWidth: 220 }}
        />
        <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto' }}>
          {data?.total ?? 0} transaction{(data?.total ?? 0) === 1 ? '' : 's'}
        </Typography>
      </Box>

      <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Merchant</TableCell>
              <TableCell align="right">Amount</TableCell>
              {/* Both body variants occupy 6 columns (category+job, or a
                  colSpan-2 status cell), so the header must span 2 here in
                  either state or the last column detaches. */}
              <TableCell colSpan={2}>
                {status === 'new' ? 'Category / Job' : 'Status'}
              </TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && [0, 1, 2, 3, 4].map((i) => (
              <TableRow key={i}><TableCell colSpan={6}><Skeleton height={28} /></TableCell></TableRow>
            ))}
            {!isLoading && !txns.length && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  {status === 'new'
                    ? 'Nothing to review. New charges land here automatically.'
                    : 'Nothing here yet.'}
                </TableCell>
              </TableRow>
            )}
            {txns.map((t) => (
              <Row key={t.id} txn={t} jobs={sortedJobs} onToast={toast} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
